import * as moment from 'moment';

import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from 'app/classes/user';
import { environment } from 'environments/environment';
import { Group } from '../models/group.model';

/**
 * Convert response for a group into a Group object.
 *
 * @param groupData - server response
 */
function convertToGroup(groupData: any) {
  const returnGroup = new Group();

  returnGroup.uuid = groupData.uuid;
  returnGroup.name = groupData.name;
  returnGroup.addOwners(groupData.owners.map(convertToUser));
  returnGroup.addUsers(groupData.members.map(convertToUser));
  returnGroup.addRequestedUsers(groupData['requested_members'].map(convertToUser));
  returnGroup.privateGroup = groupData.private;

  if (groupData.created_on_timestamp) {
    returnGroup.createdAt = moment(groupData.created_on_timestamp);
  } else {
    returnGroup.createdAt = moment();
  }

  return returnGroup;
}

/**
 * Convert response for a user into a User object.
 *
 * @param userData - server response
 * @returns User
 */
function convertToUser(userData: any): User {
  const newUser = new User()
    .setUUID(userData.uuid)
    .setFirstName(userData.firstname)
    .setLastName(userData.lastname);

  newUser.email = userData.email;
  newUser.invited = userData.invited;

  return newUser;
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(private http: HttpClient, private snackbar: MatSnackBar) {}

  handleError(e: any): Observable<never> {
    if (e.status === 409) {
      this.snackbar.open('Error: group with the provided name already exists.', 'Dismiss');
    } else {
      this.snackbar.open('Error: ' + e.message, 'Dismiss');
    }
    return throwError(e.message || 'Server error');
  }

  getGroups(): Observable<Group[]> {
    return this.get('/Group/?onlyOwned=true').pipe(
      map(
        (response: any) => response.map(convertToGroup),
        catchError(e => this.handleError(e)),
      ),
    );
  }

  getGroup(groupUuid: string): Observable<Group> {
    return this.get(`/Group/${groupUuid}`).pipe(map(convertToGroup));
  }

  getAllUsers(): Observable<User[]> {
    return this.get('/User/').pipe(
      map((response: any) => response.map(convertToUser)),
      catchError(e => this.handleError(e)),
    );
  }

  createGroup(group: Group): Observable<Group> {
    return this.post('/Group/', {
      name: group.name,
      description: '',
      private: group.privateGroup,
    }).pipe(
      map(convertToGroup),
      catchError(e => {
        console.log({e});
        return this.handleError(e);
      }),
    );
  }

  updateGroup(group: Group): Observable<void> {
    return this.put('/Group/' + group.uuid, {
      name: group.name,
      description: '',
      private: group.privateGroup,
    }).pipe(
      map(() => null),
      catchError(e => this.handleError(e)),
    );
  }

  removeMarine(groupUuid: string, memberUuid: string): Observable<void> {
    return this.delete('/Group/' + groupUuid + '/members/' + memberUuid).pipe(
      map(() => null),
      catchError(e => this.handleError(e)),
    );
  }

  removeOwner(groupUuid: string, ownerUuid: string): Observable<void> {
    return this.delete('/Group/' + groupUuid + '/owners/' + ownerUuid).pipe(
      map(() => null),
      catchError(e => this.handleError(e)),
    );
  }

  inviteMarines(groupUuid: string, emails: string[]): Observable<Group> {
    return this.post('/Group/' + groupUuid + '/invite', { emails: emails }).pipe(
      concatMap(() => this.getGroup(groupUuid)),
      catchError(e => this.handleError(e)),
    );
  }

  addOwner(groupUuid: string, userUuid: string): Observable<void> {
    return this.post('/Group/' + groupUuid + '/owners/', { uuid: userUuid }).pipe(
      map(() => null),
      catchError(e => this.handleError(e)),
    );
  }

  addOwners(groupUUID: string, userUUIDs: string[]): Observable<void> {
    const obs = [];

    userUUIDs.forEach(uuid => {
      obs.push(this.addOwner(groupUUID, uuid));
    });

    return forkJoin(obs).pipe(map(() => null), catchError(e => this.handleError(e)));
  }

  private get(route: string) {
    return this.http.get(this.buildRoute(route));
  }

  private post(route: string, data: any) {
    return this.http.post(this.buildRoute(route), data);
  }

  private buildRoute(route: string) {
    return environment.applySubstitutions(environment.api_url) + 'api' + route;
  }

  private put(route: string, data: any) {
    return this.http.put(this.buildRoute(route), data);
  }

  private delete(route: string) {
    return this.http.delete(this.buildRoute(route));
  }
}
